import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, matchPath, useLocation } from 'react-router';

import { catchAll } from '@sorare/routing';

import { ErrorBoundary } from '@sorare/error-boundary';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import {
  ANY_SPORT_MARKET_AUCTIONS,
  ANY_SPORT_MARKET_COMMON_PACKS,
  ANY_SPORT_MARKET_COMMON_PACKS_GROUP,
  ANY_SPORT_MARKET_HOME,
  ANY_SPORT_MARKET_INSTANT_BUY,
  ANY_SPORT_MARKET_MANAGER_SALES,
  ANY_SPORT_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY,
  FOOTBALL_MARKET_GO_PRO,
  MLB_MARKET_COMMON_MARKET,
  MLB_MARKET_COMMON_MARKET_ADD_CARD,
  MLB_MARKET_COMMON_MARKET_DROP_CARD,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSimplifiedNav } from '@sorare/core/src/hooks/commonGame/useSimplifiedNav';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useUnclaimedMarketTasksCount } from '@sorare/core/src/hooks/users/useUnclaimedMarketTasksCount';
import {
  glossary,
  navLabels,
  transferMarket,
} from '@sorare/core/src/lib/glossary';
import { matchAnySportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { MarketBanners } from '@sorare/marketplace/src/components/MarketBanners';
import { MarketHeader } from '@sorare/marketplace/src/components/MarketHeader';
import { DisabledMarket } from '@sorare/marketplace/src/components/market/DisabledMarket';
import { EnterFromAndroidMarketPlaceTab } from '@sorare/marketplace/src/components/mobileApp/EnterFromAndroidMarketplaceTab';
import { CurrentMarketplaceUserProvider } from '@sorare/marketplace/src/contexts/currentMarketplaceUser/Provider';
import {
  buildMarketTabRoute,
  useNavigableMarketTabParams,
} from '@sorare/marketplace/src/hooks/useNavigableMarketTabParams';

const SimplifiedNav = () => {
  const { sport, generateSportContextPath } = useSportContext();
  const {
    flags: { hideProFootball = false },
  } = useFeatureFlags();
  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();

  if (sport === Sport.BASEBALL) {
    return (
      <Tab
        to={MLB_MARKET_COMMON_MARKET_ADD_CARD}
        matches={[
          MLB_MARKET_COMMON_MARKET_ADD_CARD,
          MLB_MARKET_COMMON_MARKET_DROP_CARD,
        ]}
      >
        <FormattedMessage {...glossary.commonMarket} />
      </Tab>
    );
  }
  return (
    <>
      <Tab
        to={generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)}
        matches={[ANY_SPORT_MARKET_COMMON_PACKS]}
      >
        <Badge badgeContent={unclaimedMarketTasksCount}>
          <FormattedMessage {...navLabels.commonPacks} />
        </Badge>
      </Tab>
      {!hideProFootball && sport === Sport.FOOTBALL && (
        <Tab to={FOOTBALL_MARKET_GO_PRO} matches={[FOOTBALL_MARKET_GO_PRO]}>
          <FormattedMessage {...navLabels.pro} />
        </Tab>
      )}
    </>
  );
};

const MarketLayout = () => {
  const { sport, generateSportContextPath } = useSportContext();

  const {
    isIosApp,
    isMarketAuthorizedIosApp,
    canUseIosPostMessageAndForYou,
    isAndroidApp,
  } = useIsMobileApp();

  const {
    flags: {
      buyOnWebMarketplaceFromAndroid = false,
      useMarketHomePromoBanners = false,
    },
  } = useFeatureFlags();

  const { filterQuery, filterQueryWithInSeason } =
    useNavigableMarketTabParams();

  const { pathname } = useLocation();

  const { showSimplifiedNav } = useSimplifiedNav(sport);

  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();

  const isCommonPacksTab = !!matchAnySportPath(
    ANY_SPORT_MARKET_COMMON_PACKS,
    pathname,
    sport
  );

  const isMarketHomeTab =
    useMarketHomePromoBanners &&
    !!matchAnySportPath(ANY_SPORT_MARKET_HOME, pathname);

  const showBanner =
    !isCommonPacksTab && !showSimplifiedNav && !isMarketHomeTab;

  const matchCommonMarketRoute =
    matchAnySportPath(
      catchAll(ANY_SPORT_MARKET_COMMON_PACKS),
      pathname,
      sport
    ) || matchPath(catchAll(MLB_MARKET_COMMON_MARKET), pathname);

  const getMarketTabRoute = (route: string, inSeason?: boolean) =>
    buildMarketTabRoute(
      route,
      inSeason ? filterQueryWithInSeason : filterQuery
    );

  const disabledMarket =
    isAndroidApp && buyOnWebMarketplaceFromAndroid && !matchCommonMarketRoute;
  const content = (
    <Suspense fallback={<LoadingIndicator fullHeight />}>
      <CurrentMarketplaceUserProvider sport={sport}>
        {disabledMarket ? <DisabledMarket /> : <Outlet />}
      </CurrentMarketplaceUserProvider>
    </Suspense>
  );

  if (matchAnySportPath(ANY_SPORT_MARKET_COMMON_PACKS_GROUP, pathname, sport)) {
    return content;
  }

  return (
    <FullWidthContainer>
      <NoIndex />
      <EnterFromAndroidMarketPlaceTab />
      {!!(!isIosApp || isMarketAuthorizedIosApp) && <MarketHeader />}
      <ErrorBoundary fallback={null}>
        {!!showBanner && <MarketBanners />}
      </ErrorBoundary>

      <ContentWithStickyHeader
        hideHeader={!!isIosApp && !canUseIosPostMessageAndForYou}
        header={
          <FullWidthContainer.NoPadding>
            <TabBar fullWidth variant="flat" ignoreQueryParams>
              {showSimplifiedNav ? (
                <SimplifiedNav />
              ) : (
                <>
                  <Tab
                    to={getMarketTabRoute(
                      generateSportContextPath(ANY_SPORT_MARKET_HOME)
                    )}
                  >
                    <FormattedMessage {...navLabels.home} />
                  </Tab>
                  <Tab
                    to={getMarketTabRoute(
                      generateSportContextPath(ANY_SPORT_MARKET_AUCTIONS)
                    )}
                  >
                    <FormattedMessage {...navLabels.auctions} />
                  </Tab>
                  <Tab
                    to={getMarketTabRoute(
                      generateSportContextPath(ANY_SPORT_MARKET_INSTANT_BUY)
                    )}
                    matches={[ANY_SPORT_MARKET_INSTANT_BUY]}
                  >
                    <FormattedMessage {...transferMarket.instantBuy} />
                  </Tab>

                  <Tab
                    to={getMarketTabRoute(
                      generateSportContextPath(ANY_SPORT_MARKET_MANAGER_SALES),
                      true
                    )}
                    matches={[
                      ANY_SPORT_MARKET_MANAGER_SALES,
                      ANY_SPORT_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY,
                    ]}
                  >
                    <FormattedMessage {...navLabels.purchases} />
                  </Tab>
                  {sport === Sport.BASEBALL ? (
                    <Tab
                      to={MLB_MARKET_COMMON_MARKET_ADD_CARD}
                      matches={[
                        MLB_MARKET_COMMON_MARKET_ADD_CARD,
                        MLB_MARKET_COMMON_MARKET_DROP_CARD,
                      ]}
                    >
                      <FormattedMessage {...glossary.commonMarket} />
                    </Tab>
                  ) : (
                    <Tab
                      to={getMarketTabRoute(
                        generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)
                      )}
                      matches={[
                        ANY_SPORT_MARKET_COMMON_PACKS,
                        ANY_SPORT_MARKET_COMMON_PACKS_GROUP,
                      ]}
                    >
                      <Badge badgeContent={unclaimedMarketTasksCount}>
                        <FormattedMessage {...navLabels.commonPacks} />
                      </Badge>
                    </Tab>
                  )}
                </>
              )}
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        {content}
      </ContentWithStickyHeader>
    </FullWidthContainer>
  );
};

export default MarketLayout;
